/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Stepper, Step, StepLabel,
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Autocomplete, TextField, Chip, Stack, Alert, Button, CircularProgress,
  InputAdornment, FormControlLabel, Switch,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../Iconify';
import MappingShell, { MappingShellActions, MappingShellContent, MappingShellTitle } from './MappingShell';
import { callApi } from '../../utils/apiCall';
import { fetchDentallyPractitioners } from '../../utils/dentallyApi';
import { buildMatchSuggestions } from '../../hooks/useFuzzyMatch';
import { useSelector } from '../../redux/store';

const STEPS = ['Load & Match', 'Review', 'Done'];

// ─── Main wizard ─────────────────────────────────────────────────────────────

export default function DentallyPractitionerSyncWizard({ open, onClose, practiceId, inline }) {
  // Inline hosts (Dentally workspace → Practitioners tab) are always "open".
  const isOpen = inline || open;
  const allUsers = useSelector((s) => s.practiceUser.practiceUser.results || []);

  // Build CRM user list (enabled practice users only)
  const crmUsers = allUsers
    .filter(
      (u) =>
        u?.is_enabled &&
        u?.role !== 'ADMIN' &&
        u?.role !== 'SUPPORT' &&
        !u?.user?.is_superuser
    )
    .map((u) => ({
      id: u.id,
      name: `${u?.user?.first_name || ''} ${u?.user?.last_name || ''}`.trim(),
      dentally_practitioner_id: u.dentally_practitioner_id ?? null,
    }));

  const [activeStep, setActiveStep] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [rows, setRows] = useState([]);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [result, setResult] = useState(null);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [resyncing, setResyncing] = useState(false);

  // Full Dentally sync: fetch from Dentally API + fuzzy matching
  const runFullSync = useCallback(async () => {
    setResyncing(true);
    setLoadError(null);
    try {
      const practitioners = await fetchDentallyPractitioners(practiceId);

      const dentallyItems = practitioners.map((p) => ({
        id: p.id,
        active: p.active !== false,
        name: [p.user?.title, p.user?.first_name, p.user?.last_name].filter(Boolean).join(' '),
      }));

      const suggestions = buildMatchSuggestions(dentallyItems, crmUsers);

      const newRows = suggestions.map(({ dentallyItem, crmItem, confidence }) => {
        const alreadyLinked = crmUsers.find(
          (u) =>
            u.dentally_practitioner_id !== null &&
            Number(u.dentally_practitioner_id) === Number(dentallyItem.id)
        );
        if (alreadyLinked) {
          return {
            dentallyId: dentallyItem.id,
            dentallyName: dentallyItem.name,
            active: dentallyItem.active,
            crmUser: alreadyLinked,
            actionType: 'already_linked',
            confidence: 100,
          };
        }
        return {
          dentallyId: dentallyItem.id,
          dentallyName: dentallyItem.name,
          active: dentallyItem.active,
          crmUser: crmItem,
          actionType: crmItem ? 'match' : 'skip',
          confidence,
        };
      });

      setRows(newRows);
      setActiveStep(1);
    } catch {
      setLoadError('Failed to load practitioners. Is Dentally connected?');
    } finally {
      setResyncing(false);
    }
  }, [practiceId, crmUsers]);

  // On open: show cached linked practitioners instantly, auto-sync if none
  useEffect(() => {
    if (!isOpen || !practiceId) return;
    setActiveStep(0);
    setLoadError(null);
    setRows([]);
    setResult(null);
    setApplyError(null);
    setResyncing(false);

    // Build rows from CRM cached data (users already linked to Dentally)
    const linkedUsers = crmUsers.filter((u) => u.dentally_practitioner_id !== null);
    const cachedRows = linkedUsers.map((u) => ({
      dentallyId: u.dentally_practitioner_id,
      dentallyName: u.name,
      active: true,
      crmUser: u,
      actionType: 'already_linked',
      confidence: 100,
    }));

    setRows(cachedRows);
    setActiveStep(1);

    // If no linked practitioners, auto-trigger full sync
    if (cachedRows.length === 0) {
      runFullSync();
    }
  }, [isOpen, practiceId]);

  const updateRow = useCallback((dentallyId, updates) => {
    setRows((prev) =>
      prev.map((row) => (row.dentallyId === dentallyId ? { ...row, ...updates } : row))
    );
  }, []);

  const handleApply = async () => {
    setApplying(true);
    setApplyError(null);

    const mappings = filteredRows
      .filter((r) => r.actionType === 'match' && r.crmUser)
      .map((r) => ({ dentally_id: r.dentallyId, user_id: r.crmUser.id }));

    try {
      const res = await callApi(
        `/api/v1/practices/${practiceId}/dentally/apply-practitioner-mapping/`,
        'post',
        { mappings }
      );
      if (!res.ok) {
        setApplyError('Failed to apply mappings. Please try again.');
        return;
      }
      setResult(res.data);
      setActiveStep(2);
    } catch {
      setApplyError('An unexpected error occurred. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setRows([]);
    setResult(null);
    setApplyError(null);
    setLoadError(null);
    setSearch('');
    setActiveOnly(true);
    onClose?.();
  };

  const filteredRows = useMemo(() => {
    let result2 = rows;
    if (activeOnly) result2 = result2.filter((r) => r.active);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result2 = result2.filter((r) => r.dentallyName.toLowerCase().includes(q));
    }
    return result2;
  }, [rows, activeOnly, search]);

  const matchCount = filteredRows.filter((r) => r.actionType === 'match' && r.crmUser).length;

  return (
    <MappingShell inline={inline} open={open} onClose={handleClose} maxWidth="md">
      <MappingShellTitle><Typography variant="h6" fontWeight={700}>Sync Dentally Practitioners</Typography></MappingShellTitle>
      <MappingShellContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {activeStep === 0 && !resyncing && !loadError && (
          <Stack alignItems="center" spacing={2} py={5}>
            <CircularProgress />
            <Typography color="text.secondary">Loading practitioners and building matches…</Typography>
          </Stack>
        )}
        {activeStep === 0 && loadError && <Alert severity="error">{loadError}</Alert>}

        {activeStep === 1 && (
          <>
            {/* Re-sync button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
              <LoadingButton
                size="small"
                variant="outlined"
                loading={resyncing}
                startIcon={<Iconify icon="mdi:refresh" />}
                onClick={runFullSync}
                sx={{ textTransform: 'none' }}
              >
                Re-sync from Dentally
              </LoadingButton>
            </Box>
            {resyncing && (
              <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 2 }}>
                Re-syncing practitioners from Dentally…
              </Alert>
            )}
            {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
            {applyError && <Alert severity="error" sx={{ mb: 2 }}>{applyError}</Alert>}
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <TextField
                size="small"
                placeholder="Search practitioners…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="mdi:magnify" width={18} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControlLabel
                control={<Switch size="small" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />}
                label={<Typography variant="caption">Active only</Typography>}
                sx={{ mr: 0 }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Showing {filteredRows.length} of {rows.length} practitioners. Rows set to <strong>Skip</strong> are ignored. Greyed rows are already linked.
            </Typography>
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Dentally Practitioner</TableCell>
                    <TableCell>CRM User</TableCell>
                    <TableCell width={72}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.map((row) => (
                    <PractitionerRow
                      key={row.dentallyId}
                      row={row}
                      crmUsers={crmUsers}
                      onUpdate={updateRow}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}

        {activeStep === 2 && result && (
          <Stack spacing={2} py={2}>
            <Alert severity="success">Sync complete!</Alert>
            <Typography variant="body2">
              Practitioners linked: <strong>{result.linked}</strong>
            </Typography>
            {result.errors?.length > 0 && (
              <Alert severity="warning">
                {result.errors.length} error(s): {result.errors.join('; ')}
              </Alert>
            )}
          </Stack>
        )}
      </MappingShellContent>

      <MappingShellActions>
        {!inline && (
          <Button onClick={handleClose} color="inherit">
            {activeStep === 2 ? 'Close' : 'Cancel'}
          </Button>
        )}
        {activeStep === 1 && (
          <LoadingButton
            variant="contained"
            loading={applying}
            disabled={matchCount === 0}
            onClick={handleApply}
          >
            Link {matchCount} Practitioner(s)
          </LoadingButton>
        )}
      </MappingShellActions>
    </MappingShell>
  );
}

DentallyPractitionerSyncWizard.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  inline: PropTypes.bool,
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

DentallyPractitionerSyncWizard.defaultProps = {
  open: false,
  onClose: undefined,
  inline: false,
  practiceId: undefined,
};

// ─── Row component ────────────────────────────────────────────────────────────

function PractitionerRow({ row, crmUsers, onUpdate }) {
  const isAlreadyLinked = row.actionType === 'already_linked';
  const isSkipped = row.actionType === 'skip';

  if (isAlreadyLinked) {
    return (
      <TableRow sx={{ opacity: 0.45 }}>
        <TableCell><Typography variant="body2">{row.dentallyName}</Typography></TableCell>
        <TableCell><Typography variant="body2">{row.crmUser?.name}</Typography></TableCell>
        <TableCell><Chip label="Linked" size="small" color="success" /></TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow sx={{ opacity: isSkipped ? 0.4 : 1 }}>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">{row.dentallyName}</Typography>
          {!isSkipped && row.confidence > 0 && (
            <Chip
              label={`${row.confidence}%`}
              size="small"
              color={(() => {
                if (row.confidence >= 80) return 'success';
                if (row.confidence >= 50) return 'warning';
                return 'default';
              })()}
            />
          )}
        </Stack>
      </TableCell>

      <TableCell sx={{ minWidth: 200 }}>
        <Autocomplete
          size="small"
          options={crmUsers}
          value={row.crmUser || null}
          getOptionLabel={(opt) => opt.name || ''}
          isOptionEqualToValue={(opt, val) => opt.id === val?.id}
          renderInput={(params) => (
            <TextField {...params} placeholder="Search CRM users…" />
          )}
          onChange={(_, newValue) =>
            onUpdate(row.dentallyId, {
              crmUser: newValue,
              actionType: newValue ? 'match' : 'skip',
            })
          }
          clearOnEscape
        />
      </TableCell>

      <TableCell>
        {(!isSkipped || row.crmUser) && (
          <Button
            size="small"
            color={isSkipped ? 'primary' : 'inherit'}
            sx={{ textTransform: 'none', minWidth: 48 }}
            onClick={() => {
              let actionType;
              if (isSkipped) {
                actionType = row.crmUser ? 'match' : 'skip';
              } else {
                actionType = 'skip';
              }
              onUpdate(row.dentallyId, { actionType });
            }}
          >
            {isSkipped ? 'Undo' : 'Skip'}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

PractitionerRow.propTypes = {
  row: PropTypes.object.isRequired,
  crmUsers: PropTypes.array.isRequired,
  onUpdate: PropTypes.func.isRequired,
};
